import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { authOnboard } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, Store, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function OnboardingPage() {
  const { user, setUser, checkAuth } = useAuth();
  const presetType = sessionStorage.getItem('pharmx_signup_type');
  const [step, setStep] = useState(presetType ? 2 : 1);
  const [wsType, setWsType] = useState(presetType || '');
  const [wsName, setWsName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!wsName.trim() || wsName.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await authOnboard({
        workspace_type: wsType,
        workspace_name: wsName.trim(),
      });
      setUser(res.data);
      sessionStorage.removeItem('pharmx_signup_type');
      toast.success('Account created successfully!');
      await checkAuth();
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create account');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="onboarding-page">
      <div className="w-full max-w-xl space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">Welcome to PharmX</h1>
          <p className="text-muted-foreground mt-2">
            {user?.name ? `Hello, ${user.name}!` : 'Hello!'} Let's get you started.
          </p>
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="onboarding-step-1">
            <h2 className="text-lg font-semibold text-center">Choose your account type</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card
                className={`cursor-pointer transition-all border-2 hover:shadow-md ${
                  wsType === 'company' ? 'border-blue-500 bg-blue-500/5' : 'border-border hover:border-blue-300'
                }`}
                onClick={() => setWsType('company')}
                data-testid="ws-type-company"
              >
                <CardContent className="p-6 text-center space-y-3">
                  <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center ${
                    wsType === 'company' ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Building2 className="w-7 h-7" />
                  </div>
                  <h3 className="font-semibold text-lg">Pharma Company</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage pharmacies, deliveries & payments
                  </p>
                  <ul className="text-xs text-muted-foreground text-left space-y-1">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Manage partner pharmacies</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Track deliveries & payments</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Field team management</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Analytics & intelligence</li>
                  </ul>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer transition-all border-2 hover:shadow-md ${
                  wsType === 'pharmacy' ? 'border-emerald-500 bg-emerald-500/5' : 'border-border hover:border-emerald-300'
                }`}
                onClick={() => setWsType('pharmacy')}
                data-testid="ws-type-pharmacy"
              >
                <CardContent className="p-6 text-center space-y-3">
                  <div className={`w-14 h-14 rounded-full mx-auto flex items-center justify-center ${
                    wsType === 'pharmacy' ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Store className="w-7 h-7" />
                  </div>
                  <h3 className="font-semibold text-lg">Pharmacy (Apteka)</h3>
                  <p className="text-sm text-muted-foreground">
                    Track stock, payments & suppliers
                  </p>
                  <ul className="text-xs text-muted-foreground text-left space-y-1">
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Track your stock levels</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> View payables & payments</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Reorder suggestions</li>
                    <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Connect with suppliers</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Button
              className="w-full"
              disabled={!wsType}
              onClick={() => setStep(2)}
              data-testid="onboarding-next-btn"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-step-2">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {wsType === 'company' ? <Building2 className="w-5 h-5 text-blue-500" /> : <Store className="w-5 h-5 text-emerald-500" />}
                  Name your {wsType === 'company' ? 'company' : 'pharmacy'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder={wsType === 'company' ? 'e.g. PharmaCorp Ltd.' : 'e.g. City Pharmacy'}
                  value={wsName}
                  onChange={e => setWsName(e.target.value)}
                  className="text-lg h-12"
                  autoFocus
                  data-testid="ws-name-input"
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={loading || !wsName.trim() || wsName.trim().length < 2}
                    onClick={handleCreate}
                    data-testid="create-workspace-btn"
                  >
                    {loading ? 'Creating...' : 'Get Started'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
